/** Date helpers. Everything is rendered in the viewer's local timezone. */

const startOfDay = (d: Date) => {
  const copy = new Date(d)
  copy.setHours(0, 0, 0, 0)
  return copy
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** "Overdue by 3 days", "Today", "in 5 days" — the phrasing people scan for. */
export function describeDueDate(iso: string | null): { text: string; tone: 'danger' | 'warn' | 'muted' } {
  if (!iso) return { text: 'No date', tone: 'muted' }

  const due = startOfDay(new Date(iso))
  const today = startOfDay(new Date())
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000)

  if (days < 0) {
    const late = Math.abs(days)
    return { text: late === 1 ? 'Overdue by 1 day' : `Overdue by ${late} days`, tone: 'danger' }
  }
  if (days === 0) return { text: 'Due today', tone: 'warn' }
  if (days === 1) return { text: 'Due tomorrow', tone: 'warn' }
  if (days <= 7) return { text: `Due in ${days} days`, tone: 'muted' }
  return { text: formatDate(iso), tone: 'muted' }
}

/** For <input type="date"> which needs YYYY-MM-DD in local time. */
export function toDateInputValue(iso: string | null): string {
  if (!iso) return ''
  return dayKey(new Date(iso))
}

/**
 * Turns a date input value into an ISO timestamp at local midday. Midday avoids
 * a date silently shifting a day when the team is spread across timezones.
 */
export function fromDateInputValue(value: string): string | null {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day, 12, 0, 0).toISOString()
}

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  const seconds = Math.round((Date.now() - then) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return formatDate(iso)
}

// ---------------------------------------------------------------------------
// Days
// ---------------------------------------------------------------------------

/** Local YYYY-MM-DD. The key meetings are bucketed under to fill a day cell. */
export function dayKey(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/**
 * Day separators for the chronological list. Anchored on the calendar day, not
 * on elapsed hours, so a meeting at 23:00 tonight still reads "Today".
 */
export function formatDayHeading(iso: string): string {
  const day = startOfDay(new Date(iso))
  const today = startOfDay(new Date())
  const diff = Math.round((day.getTime() - today.getTime()) / 86_400_000)

  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'

  const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' }
  // The year is noise for the month you are looking at and essential once you
  // have paged out of it, so it only appears when it differs from today's.
  if (day.getFullYear() !== today.getFullYear()) options.year = 'numeric'
  return day.toLocaleDateString(undefined, options)
}

// ---------------------------------------------------------------------------
// Times
// ---------------------------------------------------------------------------

/** A single clock time, "14:00", in the viewer's locale and zone. */
export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/**
 * "14:00 to 15:00". The word "to" is deliberate house style, never a dash.
 * A meeting that runs past midnight has its end date spelled out, because
 * "23:30 to 00:30" on its own reads like somebody typed the times backwards.
 */
export function formatTimeRange(startIso: string, endIso: string): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const range = `${formatTime(startIso)} to ${formatTime(endIso)}`
  if (isSameDay(start, end)) return range
  return `${range} on ${end.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
}

/** "5 Aug 2026, 14:00 to 15:00". The whole answer to "when is it?" on one line. */
export function formatMeetingWhen(startIso: string, endIso: string): string {
  return `${formatDate(startIso)}, ${formatTimeRange(startIso, endIso)}`
}

/** For <input type="time"> which needs 24-hour HH:MM in local time. */
export function toTimeInputValue(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** The local midnight a YYYY-MM-DD input value names, or null if it is not one. */
function parseDateInputValue(value: string): Date | null {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

/**
 * Moves a YYYY-MM-DD input value by whole days. Built by stepping the day
 * number on a local Date, never by adding 86,400,000 milliseconds, because a day
 * is 23 or 25 hours long where a zone shifts for daylight saving.
 */
export function addDaysToDateInputValue(value: string, days: number): string {
  const base = parseDateInputValue(value)
  if (!base) return value
  return dayKey(new Date(base.getFullYear(), base.getMonth(), base.getDate() + days))
}

/** Whole days from one YYYY-MM-DD input value to another. Zero if either is blank. */
export function daysBetweenDateInputValues(from: string, to: string): number {
  const a = parseDateInputValue(from)
  const b = parseDateInputValue(to)
  if (!a || !b) return 0
  // Rounded rather than floored: the two local midnights are 23 or 25 hours
  // apart across a daylight saving boundary.
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/**
 * The pair to toDateInputValue and toTimeInputValue, built the same way
 * fromDateInputValue is: from the numeric parts, never by joining them into
 * "2026-08-05T14:00" and handing that to new Date(). That string form is parsed
 * as local time by some engines and as UTC by others, which would silently move
 * every meeting by the viewer's offset. Passing the numbers separately always
 * means that wall-clock time in the viewer's own zone, and toISOString() then
 * converts it to the UTC the server stores.
 */
export function fromDateAndTimeInputValues(dateValue: string, timeValue: string): string | null {
  if (!dateValue || !timeValue) return null
  const [year, month, day] = dateValue.split('-').map(Number)
  if (!year || !month || !day) return null

  const [hourPart, minutePart] = timeValue.split(':')
  const hour = Number(hourPart)
  const minute = Number(minutePart)
  // Not a truthiness check: midnight is 0 and perfectly valid.
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null

  return new Date(year, month - 1, day, hour, minute, 0, 0).toISOString()
}

/**
 * "Asia/Bangkok", for the quiet line telling the viewer which clock they are
 * reading. Degrades to an empty string if the browser cannot resolve a zone, so
 * the caller can simply drop the line rather than print something wrong.
 */
export function localZoneLabel(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

// ---------------------------------------------------------------------------
// Month grid
// ---------------------------------------------------------------------------

/**
 * Monday first. The group works across Asia, Europe and the US, and a
 * Monday-first grid keeps the weekend together as one block at the end of the
 * row instead of splitting it across both edges.
 */
export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

export type CalendarDay = {
  date: Date
  /** Matches dayKey, so meetings can be grouped once and looked up per cell. */
  key: string
  /** False for the leading and trailing days borrowed from the months either side. */
  inMonth: boolean
  isToday: boolean
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

/** Previous or next month. Anchored on day 1, so 31 Jan plus one month is 1 Feb. */
export function addMonths(anchor: Date, delta: number): Date {
  return new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1)
}

/** "August 2026", the heading above the grid. */
export function formatMonthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

/** The Monday on or before the first of the anchor's month. */
function gridStart(anchor: Date): Date {
  const first = startOfMonth(anchor)
  // getDay() is 0 on Sunday, so shift the week round to put Monday at 0.
  const lead = (first.getDay() + 6) % 7
  return new Date(first.getFullYear(), first.getMonth(), 1 - lead)
}

const GRID_CELLS = 42

/**
 * The six weeks of a month view. Always 42 cells, so the grid does not change
 * height as you page through the year and the rows below it stay put.
 *
 * Cells are produced by stepping the day NUMBER on a local Date and letting it
 * overflow into the neighbouring months, never by adding 86,400,000
 * milliseconds. On the day a zone shifts for daylight saving, a day is 23 or 25
 * hours long, and millisecond arithmetic quietly repeats or skips a date.
 */
export function monthGrid(anchor: Date): CalendarDay[] {
  const start = gridStart(anchor)
  const month = anchor.getMonth()
  const today = new Date()

  return Array.from({ length: GRID_CELLS }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    return {
      date,
      key: dayKey(date),
      inMonth: date.getMonth() === month,
      isToday: isSameDay(date, today),
    }
  })
}

/**
 * The window a month view has to fetch. It covers the whole visible grid rather
 * than the month alone, otherwise the borrowed days at either end would always
 * look empty. Half-open, matching the API: `to` is midnight after the last cell.
 */
export function monthGridRange(anchor: Date): { from: string; to: string } {
  const start = gridStart(anchor)
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + GRID_CELLS)
  return { from: start.toISOString(), to: end.toISOString() }
}
